import React, { memo, useCallback } from 'react'
import { Controller, useFieldArray } from 'react-hook-form'
import DeleteIcon from 'transfix-icons/build/DeleteIcon'
import AutocompleteTransfixUser, {
  IAutocompleteTransfixUserProps,
} from 'transfix-ui/components/AutocompleteTransfixUser'
import { IconButton } from 'transfix-ui/components/IconButton'
import { Box, List, ListItem, ListItemSecondaryAction, ListItemText } from 'transfix-ui/core'
import { TUseShipperRelationshipsModalFormMethods } from 'apps/marketplace/pages/shippers/components/ShipperOverview/ShipperRelationshipsModal/useShipperRelationshipsModalForm'
import { TUseCarrierRelationshipsModalFormMethods } from '../../carrier/components/GeneralInformation/Managers/CarrierRelationshipsModal/hooks/useCarrierRelationshipsModalForm'

export interface IControlledAutocompleteTransfixMultiUserProps
  extends Omit<IAutocompleteTransfixUserProps, 'onChange' | 'value'> {
  control:
    | TUseCarrierRelationshipsModalFormMethods['control']
    | TUseShipperRelationshipsModalFormMethods['control']
  label: string
  name:
    | 'secondaryManagers'
    | 'secondarySalesReps'
    | 'primaryAccountOpsRep'
    | 'secondaryAccountOpsReps'
    | 'primaryCarrierOpsRep'
    | 'secondaryCarrierOpsReps'
}

export const ControlledAutocompleteTransfixMultiUser = memo<
  IControlledAutocompleteTransfixMultiUserProps
>(({ control, disabled, label, name, ...autocompleteProps }) => {
  const { append, fields, remove } = useFieldArray({
    control,
    name,
    keyName: 'key',
  })

  const changeHandler: IAutocompleteTransfixUserProps['onChange'] = useCallback(
    value => {
      if (value && !fields.some(({ id }) => id === value.id)) {
        append(value)
      }
    },
    [append, fields]
  )

  return (
    <Box width='100%'>
      <AutocompleteTransfixUser
        {...autocompleteProps}
        disabled={disabled}
        label={label}
        value={null}
        onChange={changeHandler}
      />

      {fields.length > 0 && (
        <List>
          {fields.map((field, index) => (
            <ListItem key={field.key} divider>
              <Controller
                control={control}
                defaultValue={field.id}
                name={`${name}.${index}.id`}
                render={() => <ListItemText primary={field.text} />}
              />

              <ListItemSecondaryAction>
                <IconButton
                  disabled={disabled}
                  Icon={DeleteIcon}
                  value={field.id}
                  onClick={() => remove(index)}
                  color='tertiary'
                  label={`Remove ${label}`}
                  size='large'
                />
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  )
})
